import { useStoredAudienceId } from './useActiveAudience';
import type { AudienceId } from '@shared/constants/audiences';

type WithDefault<T> = Partial<Record<AudienceId, T>> & { default: T };

/**
 * Retourne le contenu correspondant au profil audience stocké.
 *
 * Source : le store Zustand (clé localStorage `sef_audience_store` + miroir cookie).
 * Côté SSR ou avant hydratation, retourne `content.default` pour éviter les
 * hydration mismatches.
 *
 * Bug historique corrigé : la version précédente indexait `content[audienceMeta]`
 * (objet entier) au lieu de `content[audienceId]` (la clé string), ce qui faisait
 * que le contenu retourné était TOUJOURS `content.default`, peu importe le profil.
 *
 * Usage :
 * ```tsx
 * const hero = useAudienceContent(HOME_HERO_CONTENT);
 * ```
 */
export function useAudienceContent<T>(content: WithDefault<T>): T {
  const audienceId = useStoredAudienceId();
  if (audienceId) {
    const entry = content[audienceId];
    if (entry !== undefined) return entry;
  }
  return content.default;
}
